import { useState, useEffect, useRef } from 'react';
import { faqs as staticLandingFaqs } from '../data/landingPage/faqContent';
import { faqCategories as staticFaqCategories } from '../data/faq/faqContent';
import { fetchPublicFaqs, getCachedPublicFaqs } from '../utils/faqsApi';

function buildStaticFallback() {
  return {
    landingFaqs: staticLandingFaqs.map((item, index) => ({
      id: `static-landing-${index}`,
      question: item.question,
      answer: item.answer,
    })),
    faqCategories: staticFaqCategories.map((cat, catIndex) => ({
      title: cat.title,
      items: cat.items.map((item, itemIndex) => ({
        id: `static-cat-${catIndex}-${itemIndex}`,
        question: item.question,
        answer: item.answer,
      })),
    })),
  };
}

const STATIC_FALLBACK = buildStaticFallback();

function resolveInitialState() {
  const cached = getCachedPublicFaqs();
  if (cached && (cached.landingFaqs.length > 0 || cached.faqCategories.length > 0)) {
    return cached;
  }
  return STATIC_FALLBACK;
}

/**
 * Public FAQs: instant paint (cache or static), background refresh from API.
 */
export default function usePublicFaqs() {
  const hadCacheOnMount = useRef(null);
  if (hadCacheOnMount.current === null) {
    const cached = getCachedPublicFaqs();
    hadCacheOnMount.current = Boolean(
      cached && (cached.landingFaqs.length > 0 || cached.faqCategories.length > 0)
    );
  }

  const [landingFaqs, setLandingFaqs] = useState(() => resolveInitialState().landingFaqs);
  const [faqCategories, setFaqCategories] = useState(() => resolveInitialState().faqCategories);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const payload = await fetchPublicFaqs({
          forceRefresh: hadCacheOnMount.current,
        });
        if (cancelled) return;
        if (payload.landingFaqs.length > 0) {
          setLandingFaqs(payload.landingFaqs);
        }
        if (payload.faqCategories.length > 0) {
          setFaqCategories(payload.faqCategories);
        }
      } catch {
        // Keep cache or static data on screen
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  return { landingFaqs, faqCategories };
}
